// Reading tools over one session's conversation record, for a chat opened to
// investigate a group of findings ("Investigate in chat"). They render tool
// calls and their results, which the Triage tab's own read drops: for "did
// this actually happen" the tool calls are the evidence. A chat's reads are
// not rationed.
import { Type } from "@earendil-works/pi-ai";
import { readConversationPage, citationKeysets, cursorAfter, CITATION_LEAD_MESSAGES } from "./conversationRead.js";
import { rangeToTimeRange } from "./analyticsClient.js";
import { turnText } from "./transcriptText.js";

// Messages of the record one chat will fetch. The reader returns a page and a
// cursor; a chat that takes the first page and stops is searching part of a
// session while believing it has searched all of it, and an absence found that
// way is an artefact of the read, not a fact about the session.
//
// None of it enters the model's context: it is the copy the chat's tools search
// and render windows from. Sized so a session up to it is read whole -- about
// 2.4 KB and a second or so per thousand messages on staging, and 62 of 68
// sessions behind one month's one-offs fit.
export const LITE_READ_MESSAGES = 16384;
const LITE_PAGE_MESSAGES = 1024;
const PAGE_RETRY_MS = 750;
// The slice read around one citation: the lead after it, the rest before. A
// chat's citations can sit thousands of messages apart, and one window placed
// on the newest left a fifteen-finding chat unable to find most of the others.
export const LITE_CITATION_WINDOW = 512;
// The conversation is read over a window opened well before the one the lens
// selected. That range filters findings by when their REVIEW completed, which
// is after -- sometimes long after -- the session it judged; reading the
// transcript on the same range drops the turns the finding is about.
export const LITE_READ_LEAD_MS = 30 * 24 * 3600 * 1000;
const READ_RADIUS = 4;
const READ_RADIUS_MAX = 12;
const TURN_CHARS = 900;
const CALL_CHARS = 4000;
const SEARCH_HITS = 12;

const str = (v) => (v == null ? "" : String(v));
// Unescape, drop the JSON punctuation a rendered tool call carries, collapse
// whitespace, lowercase: a quote copied out of `{"command":"git push"}` and
// one copied out of pretty-printed arguments compare equal.
export function normalizeSpan(text) {
    return str(text)
        .replace(/\\[nrt]/g, " ")
        .replace(/\\/g, " ")
        .replace(/"/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
}
const short = (id) => str(id).slice(0, 8);
const text = (body, meta) => ({ content: [{ type: "text", text: body }], ...meta });
const toolError = (message) => ({ content: [{ type: "text", text: message }], isError: true });

const clip = (s, n) => (s.length > n ? `${s.slice(0, n)} ...` : s);
// Text is matched normalized, not as it is rendered. A
// call's arguments are rendered as JSON, so a quote carrying a double quote, a
// newline or a backslash -- a third of the judge's quotes -- never matched its
// own call: in one fully read session, search found one of fifteen cited
// quotes. Both sides go through one normalizer, so a quote matches however it
// was copied.
const matchText = (s) => normalizeSpan(s);
const matchCache = new WeakMap();
const matchBodies = (turns) => {
    let bodies = matchCache.get(turns);
    if (!bodies) { bodies = turns.map((t) => matchText(turnText(t))); matchCache.set(turns, bodies); }
    return bodies;
};

// The findings a group holds: what the judge raised against one session.
export const jobFindings = (job) => (Array.isArray(job?.findings) && job.findings.length ? job.findings : [job]);

// Where the group's citation sits in the record.
function locateIn(turns, finding) {
    if (finding.messages?.length) {
        const wanted = new Set(finding.messages);
        const i = turns.findIndex((t) => wanted.has(str(t.messageId).toLowerCase()));
        if (i >= 0) return { at: i, how: "the cited message id" };
    }
    const quotes = (finding.quotes || []).map(matchText).filter(Boolean);
    const needles = quotes.flatMap((q) => [q.slice(0, 60), q.slice(0, 30)]).filter((n) => n.length >= 12);
    const bodies = matchBodies(turns);
    for (const n of needles) {
        const i = bodies.findIndex((b) => b.includes(n));
        if (i >= 0) return { at: i, how: "the cited words" };
    }
    return { at: -1, how: "" };
}

// The turns around a point, rendered.
export function renderWindow(turns, at, radius, session) {
    const from = Math.max(0, at - radius);
    const to = Math.min(turns.length - 1, at + radius);
    const lines = [`session ${session} - ${turns.length} messages - centred on T${at + 1}`];
    for (let i = from; i <= to; i++) {
        const t = turns[i];
        lines.push(`T${i + 1} ${t.role}${t.messageId ? ` [${t.messageId}]` : ""}${i === at ? " <<" : ""}: ${clip(turnText(t).replace(/\s+/g, " ").trim(), TURN_CHARS)}`);
    }
    return lines.join("\n");
}

// The rubric, ported from the harness's code-mode prompt -- the arm that
// scored 23 of 36 against the labelled set. The API paragraph is ours because
// the tools differ; the SEMANTICS are the harness's, and they are the part
// that was paid for. An earlier version of this file paraphrased them into a
// sketch and lost every operative rule, which is how three runs came to
// dismiss two thirds of their findings.
// Marks the join between two slices of the record, so a chat never reads the
// last turn of one as the neighbour of the first turn of the next.
export const GAP_TEXT = "(messages between here and the next turn were not loaded)";
const gapTurn = () => ({ role: "gap", messageId: null, blocks: [{ type: "text", text: GAP_TEXT }] });

// Whether the record read places a page on a message by id; null until a
// server has answered. One that predates it serves the newest page instead,
// and asking again on every citation would spend a read each time to learn
// the same thing.
let recordPlaces = null;
export function _resetRecordPlacementForTests() {
    recordPlaces = null;
}

// The record a chat reads. The newest page comes first: it is the only page
// that carries the record's size, and a session that fits the budget is then
// read back from it whole, with nothing to place and no gaps to warn about.
// Slices were read even for those, and a session of 3,801 messages came back
// as 516 of them in eight overlapping pieces, marked incomplete.
//
// A session larger than the budget keeps its newest page, then a slice around
// each cluster of its citations, oldest first, with what budget is left spent
// reading further back from the oldest. Citations are taken newest first; one
// already inside a slice read for another costs nothing, so a chat whose
// citations sit together reads what a single window would. Extending only the
// oldest slice keeps slices from running into each other.
//
// A slice is placed by the record on the cited message itself where the
// server can. The analytics position the keyset path anchors on is where
// analytics keeps a re-shipped message, not where the record does, and a
// slice read from there can miss the message it was read for. `missing` is
// the cited messages the record says it does not hold in the window.
// Returns { turns, total, complete, seeked, slices, placed, missing }.
export async function readCitedRecord({
    traceId, timeRange, messageIds = [], readPage = readConversationPage,
    keysets = citationKeysets, anchorAfter = cursorAfter,
    budget = LITE_READ_MESSAGES, window = LITE_CITATION_WINDOW, page = LITE_PAGE_MESSAGES,
    place = recordPlaces !== false, retryMs = PAGE_RETRY_MS,
}) {
    // A page the gateway fails is read once more before it fails the chat. A
    // session read whole is a dozen pages or more, and a single transient 502
    // among them used to sink the chat and send it round again from the start.
    const readOnce = readPage;
    readPage = async (args) => {
        try {
            return await readOnce(args);
        } catch (error) {
            if (!(Number(error?.status) >= 500)) throw error;
            await new Promise((resolve) => setTimeout(resolve, retryMs));
            return readOnce(args);
        }
    };
    // A read failure here is the chat's failure, not a missing slice.
    const first = await readPage({ traceId, timeRange, cursor: null, maxMessages: Math.min(page, budget) });
    let total = Number(first?.total) || 0;
    let left = budget - Math.min(page, budget);
    const newest = { turns: Array.isArray(first?.turns) ? first.turns : [], next: first?.nextCursor || null, fromNewest: true };
    if (!newest.next || (total && total <= budget)) {
        while (left > 0 && newest.next) {
            const got = await readPage({ traceId, timeRange, cursor: newest.next, maxMessages: Math.min(page, left) });
            left -= Math.min(page, left);
            newest.turns = [...(Array.isArray(got?.turns) ? got.turns : []), ...newest.turns];
            newest.next = got?.nextCursor || null;
        }
        if (!newest.next) {
            return { turns: newest.turns, total: total || newest.turns.length, complete: true, seeked: false, slices: 1, placed: 0, missing: [] };
        }
        // The count said it would fit and the pages kept coming: read on as a
        // session too large for the budget, keeping what is already held.
    }
    let located = [];
    try {
        located = await keysets({ traceId, timeRange, messageIds });
    } catch {
        /* the newest page still reads; a failed seek is not a failed chat */
    }
    // A citation analytics cannot place can still be one the record holds.
    const known = new Set(located.map((c) => str(c.messageId).toLowerCase()));
    const unplaced = place
        ? [...new Set(messageIds.map((id) => str(id).toLowerCase()).filter((id) => id && !known.has(id)))].map((messageId) => ({ messageId }))
        : [];
    const cites = [...located, ...unplaced];
    let placed = 0;
    const missing = [];
    const idsOf = (turns) => new Set(turns.map((t) => str(t.messageId).toLowerCase()).filter(Boolean));
    // Newest slice first while reading.
    const slices = [newest];
    const covered = idsOf(newest.turns);
    // An anchor already read from is a slice already held; reading it again
    // spends budget on nothing new.
    const anchors = new Set(["newest"]);
    for (const cite of cites) {
        if (left <= 0) break;
        if (covered.has(cite.messageId)) continue;
        if (place && recordPlaces !== false) {
            let got = null;
            try {
                got = await readPage({ traceId, timeRange, aroundMessageId: cite.messageId, following: Math.min(CITATION_LEAD_MESSAGES, Math.floor(window / 4)), maxMessages: Math.min(window, left) });
            } catch (error) {
                // The record refuses a message it does not hold in the window;
                // anything else is the read failing, and fails the chat.
                if (error?.status !== 400) throw error;
                missing.push(cite.messageId);
                covered.add(cite.messageId);
                continue;
            }
            left -= Math.min(window, left);
            total = Number(got?.total) || total;
            const turns = Array.isArray(got?.turns) ? got.turns : [];
            for (const id of idsOf(turns)) covered.add(id);
            if (got?.aroundHonoured) {
                covered.add(cite.messageId);
                recordPlaces = true;
                placed += 1;
                slices.push({ turns, next: got?.nextCursor || null, fromNewest: false });
                continue;
            }
            // An older server read the newest page instead, which is already
            // held. Every citation from here on anchors the old way.
            recordPlaces = false;
            if (covered.has(cite.messageId)) continue;
        }
        if (!cite.eventTime) continue;
        let cursor = null;
        try {
            cursor = await anchorAfter({ traceId, timeRange, eventTime: cite.eventTime, ordinal: cite.ordinal });
        } catch {
            continue;
        }
        const at = cursor ? JSON.stringify(cursor) : "newest";
        if (anchors.has(at)) { covered.add(cite.messageId); continue; }
        anchors.add(at);
        // A read failure here is the chat's failure, not a missing slice.
        const got = await readPage({ traceId, timeRange, cursor, maxMessages: Math.min(window, left) });
        left -= Math.min(window, left);
        total = Number(got?.total) || total;
        const turns = Array.isArray(got?.turns) ? got.turns : [];
        covered.add(cite.messageId);
        for (const id of idsOf(turns)) covered.add(id);
        slices.push({ turns, next: got?.nextCursor || null, fromNewest: !cursor });
    }
    const oldest = slices[slices.length - 1];
    while (left > 0 && oldest.next) {
        const got = await readPage({ traceId, timeRange, cursor: oldest.next, maxMessages: Math.min(page, left) });
        left -= Math.min(page, left);
        total = Number(got?.total) || total;
        oldest.turns = [...(Array.isArray(got?.turns) ? got.turns : []), ...oldest.turns];
        oldest.next = got?.nextCursor || null;
    }
    // Oldest first. A slice read before a newer one's citation can reach into
    // it; the overlap is dropped from the newer, and slices that share no
    // message are marked as apart.
    const turns = [];
    const seen = new Set();
    slices.reverse().forEach((slice, i) => {
        const fresh = slice.turns.filter((t) => !t.messageId || !seen.has(str(t.messageId).toLowerCase()));
        const touches = fresh.length < slice.turns.length;
        if (i > 0 && !touches) turns.push(gapTurn());
        for (const t of fresh) {
            if (t.messageId) seen.add(str(t.messageId).toLowerCase());
            turns.push(t);
        }
    });
    const gaps = turns.some((t) => t.role === "gap");
    const complete = !gaps && !oldest.next && slices[slices.length - 1].fromNewest;
    const read = turns.filter((t) => t.role !== "gap").length;
    return { turns, total: total || read, complete, seeked: located.length > 0 || placed > 0, slices: slices.length, placed, missing };
}

export function createReadTools({ finding, context = {}, readPage = readConversationPage, keysets = citationKeysets, anchorAfter = cursorAfter }) {
    // `finding` is a group: one session, and the findings raised against it.
    const members = jobFindings(finding);
    const timeRange = context.window?.fromMs && context.window?.toMs
        ? rangeToTimeRange({
            from: new Date(context.window.fromMs - LITE_READ_LEAD_MS).toISOString(),
            to: new Date(context.window.toMs).toISOString(),
        })
        : rangeToTimeRange(null);
    let cached = null;
    // The whole record this chat is entitled to, not its first page. `total`
    // and `exhausted` come back with every page; carrying them lets the chat be
    // told what it is missing instead of inferring a session from a fragment.
    const loadRecord = () => {
        if (!cached) cached = readCitedRecord({
            traceId: str(finding.session).toLowerCase(),
            timeRange,
            messageIds: members.flatMap((f) => f.messages || []),
            readPage, keysets, anchorAfter,
        });
        return cached;
    };
    const loadTurns = () => loadRecord().then((r) => r.turns);

    const locate = (turns) => locateIn(turns, finding);

    const readTurns = {
        name: "read_turns",
        description: `The session's turns around a point, with tool calls and their results rendered. Centre on the finding's citation (default), a quote, or a turn number. Radius default ${READ_RADIUS}, max ${READ_RADIUS_MAX}.`,
        parameters: Type.Object({
            around: Type.Optional(Type.String({ description: "a quote to centre on; omit to centre on the finding's citation" })),
            turn: Type.Optional(Type.Number({ description: "a turn number to centre on (1-based)" })),
            radius: Type.Optional(Type.Number()),
        }),
        async execute(_id, args) {
            let turns;
            try {
                turns = await loadTurns();
            } catch (error) {
                return toolError(`Could not read trace ${short(finding.session)}: ${error?.message || error}`);
            }
            if (!turns.length) return text(`Session ${short(finding.session)} has no readable messages in this window.`);
            let at = -1;
            let how = "";
            if (Number.isFinite(args.turn) && args.turn > 0) {
                at = Math.min(turns.length - 1, Math.floor(args.turn) - 1);
                how = `turn ${args.turn}`;
            } else if (args.around) {
                const needle = matchText(args.around).slice(0, 60);
                at = matchBodies(turns).findIndex((b) => b.includes(needle));
                how = "the quote";
                if (at < 0) return text(`Nothing in this session's ${turns.length} messages matches that quote.`);
            } else {
                const found = locate(turns);
                at = found.at;
                how = found.how;
                if (at < 0) return text(`The finding's citation is not in this session's ${turns.length} readable messages. It may be older than the window holds.`);
            }
            const radius = Math.max(1, Math.min(READ_RADIUS_MAX, Number(args.radius) || READ_RADIUS));
            const located = how ? `located by ${how}` + String.fromCharCode(10) : "";
            return text((located + renderWindow(turns, at, radius, finding.session)));
        },
    };

    const listCalls = {
        name: "list_calls",
        description: "Every tool call in the session, in order: turn, name, whether it errored, and the first of its arguments. The way to find an act when the citation does not land on it.",
        parameters: Type.Object({
            name: Type.Optional(Type.String({ description: "only calls of this tool" })),
            offset: Type.Optional(Type.Number()),
            limit: Type.Optional(Type.Number({ description: "default 30, max 60" })),
        }),
        async execute(_id, args) {
            const turns = await loadTurns();
            const want = str(args.name).toLowerCase();
            const rows = [];
            for (let i = 0; i < turns.length; i++) {
                for (const b of turns[i].blocks || []) {
                    if (b.type !== "tool_use") continue;
                    const name = b.name || "?";
                    if (want && name.toLowerCase() !== want) continue;
                    const input = b.input == null ? "(arguments not captured)" : clip(JSON.stringify(b.input), 160);
                    rows.push(`T${i + 1} ${name}: ${input}`);
                }
            }
            const offset = Math.max(0, Number(args.offset) || 0);
            const limit = Math.max(1, Math.min(60, Number(args.limit) || 30));
            const page = rows.slice(offset, offset + limit);
            if (!rows.length) return text(want ? `No ${want} calls in this session.` : "No tool calls in this session.");
            return text(([`${rows.length} tool call(s); showing ${offset + 1}-${offset + page.length}`, ...page].join(String.fromCharCode(10))));
        },
    };

    const readCall = {
        name: "read_call",
        description: `One tool call on a turn, in full: what it was given and what it returned, up to ${CALL_CHARS} characters a side. Use it whenever the act in question is something the actor ran.`,
        parameters: Type.Object({
            turn: Type.Number({ description: "the turn number the call is on (from read_turns)" }),
            index: Type.Optional(Type.Number({ description: "which call on that turn, 1-based; default the first" })),
        }),
        async execute(_id, args) {
            const turns = await loadTurns();
            const i = Math.floor(Number(args.turn)) - 1;
            if (!(i >= 0 && i < turns.length)) return toolError(`No turn ${args.turn} in this trace's ${turns.length} messages.`);
            const blocks = (turns[i].blocks || []).filter((b) => b.type === "tool_use" || b.type === "tool_result");
            if (!blocks.length) return text(`T${i + 1} carries no tool call.`);
            const pick = Math.max(1, Math.floor(Number(args.index) || 1));
            const block = blocks[pick - 1];
            if (!block) return text(`T${i + 1} carries ${blocks.length} tool blocks; ${pick} is past the end.`);
            const lines = [`T${i + 1} - ${blocks.length} tool block(s) - showing ${pick}`];
            if (block.type === "tool_use") {
                lines.push(`tool: ${block.name || "?"}`);
                lines.push(block.input === undefined || block.input === null
                    ? "arguments: not captured (this says nothing about whether the act happened)"
                    : `arguments: ${clip(JSON.stringify(block.input, null, 1), CALL_CHARS)}`);
            } else {
                lines.push(`result of: ${block.tool_name || "?"}${block.isError ? " (error)" : ""}`);
                const body = typeof block.content === "string" ? block.content : JSON.stringify(block.content ?? null, null, 1);
                lines.push(body ? `result: ${clip(body, CALL_CHARS)}` : "result: not captured");
            }
            return text((lines.join("\n")));
        },
    };

    const searchSession = {
        name: "search_session",
        description: `Find text anywhere in the session, tool calls and results included. Returns up to ${SEARCH_HITS} turns with the matching line.`,
        parameters: Type.Object({ query: Type.String({ description: "the text to find" }) }),
        async execute(_id, args) {
            const needle = matchText(args.query);
            if (needle.length < 3) return toolError("Give at least three characters to search for.");
            const turns = await loadTurns();
            const bodies = matchBodies(turns);
            const hits = [];
            for (let i = 0; i < turns.length && hits.length < SEARCH_HITS; i++) {
                const at = bodies[i].indexOf(needle);
                if (at < 0) continue;
                // The hit is shown as the session holds it. The match was on
                // normalized text, so its offset is carried over in proportion.
                const raw = turnText(turns[i]).replace(/\s+/g, " ").trim();
                const from = Math.max(0, Math.round((at / Math.max(1, bodies[i].length)) * raw.length) - 80);
                hits.push(`T${i + 1} ${turns[i].role}: ...${clip(raw.slice(from), 260)}`);
            }
            if (!hits.length) return text(`Nothing in this session's ${turns.length} messages matches "${str(args.query).slice(0, 60)}". An absence here is worth recording: it is what makes a contradiction more than a guess.`);
            return text(([`${hits.length} match(es):`, ...hits].join("\n")));
        },
    };

    return [readTurns, listCalls, readCall, searchSession];
}
